<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import isEmpty from "lodash/isEmpty";
import { onBeforeUnmount } from "vue";
import { toggleAddToCartButtonState } from "@/functions/dom";
import ShapeUpload from "./ShapeUpload.vue";

interface Props {
	primitives: {
		font: string|boolean;
	};
	hasFailedLoadingFont?: boolean;

	unit: string;
	onResize: ({ canPreview }) => void;
}

const props = defineProps<Props>();
const store = useStore();

const previewContainer = ref<HTMLElement | null>(null);
const isDragging = ref(false);
const dragStart = ref({ x: null, y: null });

const hasValidatorErrors = computed(() => store.getters.hasValidatorErrors);
const previewModel = computed(() => store.getters.getPreviewModel);
const canPreview = computed(() => store.getters.isShapeComplete);
const shape = computed(() => store.getters.shape);
const isLoading = computed(() => {
	if(!store.getters.isShapeComplete) {
		return false;
	}
	const completeModel = store.getters.getExportableModel;

	return isEmpty(completeModel) || isEmpty(completeModel.models);
});
const fontIsLoading = computed(() => store.getters.fontIsLoading);
const translation = computed(() => store.getters.translation);
const isShapeComplete = computed(() => store.getters.isShapeComplete);
const transformContainerStyle = computed(() => {
	return {
		transform: `translate(${translation.value[ 0 ]}px, ${translation.value[ 1 ]}px)`,
	};
});
const zoom = computed(() => store.getters.zoom);
const rotation = computed(() => store.getters.rotation);
const previewTransformStyle = computed(() => {
	return {
		display: 'flex',
		transform: `rotate(${rotation.value}deg) scale(${zoom.value})`,
		transition: "transform 0.5s ease 0s",
		transformOrigin: "center",
	};
});

const resizeObserver = new ResizeObserver(() => {
	updatePreviewShapeDimensions();
});

watch(hasValidatorErrors, (hasErrors: boolean) => {
	toggleAddToCartButtonState(hasErrors);
});

watch(shape, async (newValue, oldValue) => {
	if (newValue !== oldValue) {
		updatePreviewShapeDimensions();
	}
}, { deep: true });

onMounted(() => {
	if (previewContainer.value) {
		resizeObserver.observe(previewContainer.value);
		updatePreviewShapeDimensions();
	}
	store.dispatch('loadConfiguration');
});

onBeforeUnmount(() => {
	resizeObserver.disconnect();
});

function updatePreviewShapeDimensions(): void {
	if (!previewContainer.value) return;

	store.commit("updatePreviewShapeDimensions", {
		width: previewContainer.value.offsetWidth,
		height: previewContainer.value.offsetHeight,
	});

	if (props.onResize) {
		props.onResize({ canPreview: canPreview.value });
	}
}

function mousedown( event: MouseEvent ):void {
	if (!canPreview.value) return;

	dragStart.value.x = event.screenX;
	dragStart.value.y = event.screenY;
	isDragging.value = true;
}

function mouseup(): void {
	isDragging.value = false;
}

function mousemove( event: MouseEvent ): void {
	if (!isDragging.value) return;

	const diffX = event.screenX - dragStart.value.x;
	const diffY = event.screenY - dragStart.value.y;

	store.commit('updateTranslation', [
		translation.value[0] + diffX,
		translation.value[1] + diffY,
	] );

	dragStart.value.x = event.screenX;
	dragStart.value.y = event.screenY;
}

</script>

<template>
	<div
		ref="previewContainer"
		class="measurements-selector__sub-section measurements-selector__preview"
		:style="{'cursor': isDragging ? 'grabbing' : 'grab'}"
		@mousedown="mousedown"
		@mouseup="mouseup"
		@mousemove="mousemove"
	>
		<span
			v-if="props.hasFailedLoadingFont === props.primitives.font"
			class="measurements-selector__message"
		>
			{{ $i18n.messages.failedLoadingFont }}
		</span>
		<span v-else-if="fontIsLoading" class="measurements-selector__message">
			<i class="icon-spinner2"></i> {{ $i18n.messages.isLoadingFont }}
		</span>
		<span v-else-if="isLoading" class="measurements-selector__message">
			<i class="icon-spinner2"></i> {{ $i18n.messages.isLoadingCalculations }}
		</span>
		<div
			v-else-if="isShapeComplete"
			class="measurements-selector__preview-inner-container"
			:style="transformContainerStyle"
		>
			<div :style="previewTransformStyle" v-html="previewModel"></div>
		</div>
		<ShapeUpload
			v-else-if="shape === 'dxf'"
			:isInPreview="true"
		/>
		<span v-else class="measurements-selector__message">
			{{ $i18n.messages.noPreview }}
		</span>
	</div>
</template>
